import type { ApprovalActionRecord, ApprovalInstance } from '@/types/approvals';
import type { EntityTimelineTarget } from '@/modules/approvals/entityApprovalTimeline.types';

const RETURN_ACTIONS = new Set(['RETURN', 'REQUEST_CHANGES']);

/** Entity types that support an inline edit form before resubmit. */
const RETURNED_EDIT_ENTITY_TYPES = new Set(['LEAVE', 'ATTENDANCE']);

export function supportsReturnedEntityEdit(entityType: string | undefined): boolean {
  if (!entityType) return false;
  return RETURNED_EDIT_ENTITY_TYPES.has(entityType.toUpperCase());
}

export function isLeaveApprovalReturned(
  instance: Pick<ApprovalInstance, 'approvalStatus'> | null | undefined,
  recordStatusCode?: string | null,
): boolean {
  return (
    instance?.approvalStatus === 'RETURNED' ||
    (recordStatusCode ?? '').toUpperCase() === 'RETURNED'
  );
}

/** @deprecated Use isLeaveApprovalReturned — kept for call-site clarity. */
export const isApprovalReturned = isLeaveApprovalReturned;

/** True when the signed-in user is the employee the record belongs to (not merely the approval submitter). */
export function isLeaveSubjectEmployee(options: {
  currentEmployeeId?: number | null;
  instance?: Pick<ApprovalInstance, 'requesterEmployeeId'> | null;
  subjectEmployeeId?: number | null;
}): boolean {
  const { currentEmployeeId, instance, subjectEmployeeId } = options;
  const ownerEmployeeId = subjectEmployeeId ?? instance?.requesterEmployeeId;
  return (
    currentEmployeeId != null &&
    ownerEmployeeId != null &&
    currentEmployeeId === ownerEmployeeId
  );
}

/** True when the signed-in user may edit and resubmit (submitter or subject employee). */
export function isLeaveOwnerUser(options: {
  currentUserId?: number | null;
  currentEmployeeId?: number | null;
  instance?: Pick<ApprovalInstance, 'requestedBy' | 'requesterEmployeeId'> | null;
  subjectEmployeeId?: number | null;
}): boolean {
  const { currentUserId, currentEmployeeId, instance, subjectEmployeeId } = options;
  if (currentUserId != null && instance?.requestedBy === currentUserId) {
    return true;
  }
  const ownerEmployeeId = subjectEmployeeId ?? instance?.requesterEmployeeId;
  if (currentEmployeeId != null && ownerEmployeeId != null && currentEmployeeId === ownerEmployeeId) {
    return true;
  }
  return false;
}

export function buildReturnedEditTimelineTarget(
  target: Omit<EntityTimelineTarget, 'autoEditReturned' | 'allowRequesterView'>,
  options?: { isSubjectEmployee?: boolean },
): EntityTimelineTarget {
  const isLeave = target.entityType.toUpperCase() === 'LEAVE';
  const useEss = target.essScope ?? (isLeave && options?.isSubjectEmployee === true);
  return {
    ...target,
    essScope: useEss,
    allowRequesterView: true,
    autoEditReturned: true,
    title: target.title ?? 'Edit & resubmit',
  };
}

/** Opens edit mode for returned records owned by the current user; otherwise a read-only timeline. */
export function buildEntityTimelineTarget(
  target: Omit<EntityTimelineTarget, 'autoEditReturned' | 'allowRequesterView'>,
  options: {
    isRecordOwner: boolean;
    isSubjectEmployee?: boolean;
    isReturned: boolean;
  },
): EntityTimelineTarget {
  const isSubjectEmployee = options.isSubjectEmployee ?? options.isRecordOwner;
  if (options.isReturned && isSubjectEmployee && supportsReturnedEntityEdit(target.entityType)) {
    return buildReturnedEditTimelineTarget(target, { isSubjectEmployee });
  }
  return {
    ...target,
    allowRequesterView: true,
    autoEditReturned: false,
  };
}

/** @deprecated Use buildEntityTimelineTarget */
export function buildLeaveTimelineTarget(
  target: Omit<EntityTimelineTarget, 'autoEditReturned' | 'allowRequesterView'>,
  options: { isLeaveOwner: boolean; isReturned: boolean },
): EntityTimelineTarget {
  return buildEntityTimelineTarget(target, {
    isRecordOwner: options.isLeaveOwner,
    isReturned: options.isReturned,
  });
}

export function getLastReturnComment(timeline: ApprovalActionRecord[] | undefined): string | null {
  if (!timeline?.length) return null;
  for (let i = timeline.length - 1; i >= 0; i -= 1) {
    const action = timeline[i];
    if (RETURN_ACTIONS.has(action.action?.toUpperCase() ?? '')) {
      const comment = action.comments?.trim();
      if (comment) return comment;
    }
  }
  return null;
}

export function essLeaveEditPath(leaveId: number): string {
  return `/hr/ess?tab=leave&editLeave=${leaveId}`;
}
